import React from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Input,
  Button,
  Table,
  TableHeader,
  TableBody,
  TableColumn,
  TableRow,
  TableCell,
  Spinner,
} from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";
import { useUser } from "@webuild-ai/react-auth";

import { useWebuildAPIClient } from "@/providers/WeBuildClientProvider";
import { PageTitle } from "@/components/UI/PageTitle";
import { logger } from "@/telemetry";

// MCP Server type (adjust if you have a type from SDK)
type MCPServer = {
  id: string;
  tenant: string;
  name: string;
  command: string;
  args: string[];
  readTimeoutSeconds: number;
  env: { [key: string]: string };
};

const defaultServer: Omit<MCPServer, "id" | "tenant"> = {
  name: "",
  command: "",
  args: [],
  readTimeoutSeconds: 60,
  env: {},
};

export const DataHubMCPServerPage: React.FC = () => {
  const { platformClient } = useWebuildAPIClient();
  const { userProfile } = useUser();
  const [servers, setServers] = React.useState<MCPServer[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [form, setForm] = React.useState<Omit<MCPServer, "id" | "tenant">>({
    ...defaultServer,
  });
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [argsInput, setArgsInput] = React.useState("");
  const [envList, setEnvList] = React.useState<{ key: string; value: string }[]>([]);
  const { t } = useTranslation();

  const fetchServers = React.useCallback(async () => {
    if (!platformClient) return;
    setIsLoading(true);
    try {
      // Use mcpServers API
      const res = await platformClient.mcpServers?.list();

      setServers(res || []);
    } catch (err) {
      logger.error("Error fetching servers", err);
    } finally {
      setIsLoading(false);
    }
  }, [platformClient]);

  React.useEffect(() => {
    if (platformClient) fetchServers();
  }, [platformClient, fetchServers]);

  // Keep form.args and argsInput in sync
  React.useEffect(() => {
    setArgsInput(form.args.join(", "));
  }, [form.args]);

  React.useEffect(() => {
    setEnvList(Object.entries(form.env).map(([key, value]) => ({ key, value })));
  }, [form.env]);

  const handleInputChange = (field: keyof typeof defaultServer, value: string | number) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleArgsChange = (value: string) => {
    setArgsInput(value);
    setForm((prev) => ({
      ...prev,
      args: value
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
    }));
  };

  const handleEnvChange = (idx: number, key: string, value: string) => {
    setEnvList((prev) => {
      const updated = [...prev];

      updated[idx] = { key, value };

      return updated;
    });
  };

  const handleAddEnv = () => {
    setEnvList((prev) => [...prev, { key: "", value: "" }]);
  };

  const handleRemoveEnv = (idx: number) => {
    setEnvList((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      // Build env object from envList
      const env = Object.fromEntries(envList.filter((e) => e.key).map((e) => [e.key, e.value]));

      // Pass a full MCPServer object for create (id/tenant empty)
      await platformClient?.mcpServers?.create({
        id: form.name,
        tenant: userProfile?.tenant || "",
        name: form.name,
        command: form.command,
        args: form.args,
        readTimeoutSeconds: form.readTimeoutSeconds,
        env,
      });
      setForm(defaultServer);
      setArgsInput("");
      setEnvList([]);
      await fetchServers();
    } catch (err) {
      logger.error("Error creating server", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      // Use mcpServers API, pass { serverId: id }
      await platformClient?.mcpServers?.delete({ serverId: id });
      await fetchServers();
    } catch (err) {
      logger.error("Error deleting server", err);
    }
  };

  return (
    <div className="w-full p-4 space-y-6">
      <PageTitle
        centered
        dataId="mcp-server-title"
        icon="mdi:server"
        title="MCP Server Management"
      />
      <Card>
        <CardHeader className="flex flex-col gap-1">
          <h2 className="text-xl font-semibold">Add New MCP Server</h2>
        </CardHeader>
        <CardBody>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                required
                label="Name"
                value={form.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
              />
              <Input
                required
                label="Command"
                value={form.command}
                onChange={(e) => handleInputChange("command", e.target.value)}
              />
              <Input
                label="Args (comma separated)"
                placeholder="arg1, arg2, arg3"
                value={argsInput}
                onChange={(e) => handleArgsChange(e.target.value)}
              />
              <Input
                required
                label="Read Timeout (seconds)"
                min={1}
                type="number"
                value={form.readTimeoutSeconds.toString()}
                onChange={(e) =>
                  handleInputChange("readTimeoutSeconds", parseInt(e.target.value) || 0)
                }
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold">Environment Variables</span>
                <Button size="sm" type="button" onPress={handleAddEnv}>
                  Add
                </Button>
              </div>
              {envList.length === 0 && (
                <div className="text-sm text-gray-500">No environment variables</div>
              )}
              {envList.map((env, idx) => (
                <div key={idx} className="flex gap-2 mb-2">
                  <Input
                    className="w-1/2"
                    label="Key"
                    value={env.key}
                    onChange={(e) => handleEnvChange(idx, e.target.value, env.value)}
                  />
                  <Input
                    className="w-1/2"
                    label="Value"
                    value={env.value}
                    onChange={(e) => handleEnvChange(idx, env.key, e.target.value)}
                  />
                  <Button
                    color="danger"
                    size="sm"
                    type="button"
                    onPress={() => handleRemoveEnv(idx)}
                  >
                    Remove
                  </Button>
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-4">
              <Button
                color="default"
                type="button"
                variant="light"
                onPress={() => {
                  setForm(defaultServer);
                  setArgsInput("");
                  setEnvList([]);
                }}
              >
                Cancel
              </Button>
              <Button className="btn-brand-primary" isLoading={isSubmitting} type="submit">
                Add Server
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>
      <Card>
        <CardHeader>
          <h2 className="text-xl font-semibold">{t("MCP Servers")}</h2>
        </CardHeader>
        <CardBody>
          <Table aria-label={t("MCP Servers table")}>
            <TableHeader>
              <TableColumn>{t("ID")}</TableColumn>
              <TableColumn>{t("Tenant")}</TableColumn>
              <TableColumn>{t("Name")}</TableColumn>
              <TableColumn>{t("Command")}</TableColumn>
              <TableColumn>{t("Args")}</TableColumn>
              <TableColumn>{t("Timeout")}</TableColumn>
              <TableColumn>{t("Env")}</TableColumn>
              <TableColumn>{t("Actions")}</TableColumn>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell className="text-center py-8" colSpan={8}>
                    <div className="flex items-center justify-center gap-2">
                      <Spinner size="sm" />
                      <span>{t("Loading servers...")}</span>
                    </div>
                  </TableCell>
                </TableRow>
              ) : servers.length === 0 ? (
                <TableRow>
                  <TableCell className="text-center py-8" colSpan={8}>
                    {t("No servers found")}
                  </TableCell>
                </TableRow>
              ) : (
                servers.map((server) => (
                  <TableRow key={server.id}>
                    <TableCell>{server.id}</TableCell>
                    <TableCell>{server.tenant}</TableCell>
                    <TableCell>{server.name}</TableCell>
                    <TableCell>{server.command}</TableCell>
                    <TableCell>{server.args.join(", ")}</TableCell>
                    <TableCell>{server.readTimeoutSeconds}</TableCell>
                    <TableCell>
                      {Object.entries(server.env).length === 0 ? (
                        <span className="text-xs text-gray-400">None</span>
                      ) : (
                        <ul className="text-xs">
                          {Object.entries(server.env).map(([k, v]) => (
                            <li key={k}>
                              <span className="font-semibold">{k}</span>: {v}
                            </li>
                          ))}
                        </ul>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        isIconOnly
                        aria-label={`Delete ${server.name}`}
                        color="danger"
                        onPress={() => handleDelete(server.id)}
                      >
                        <Icon className="text-lg" icon="gravity-ui:trash-bin" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardBody>
      </Card>
    </div>
  );
};

export default DataHubMCPServerPage;
